import { mountRazReading } from "../raz-reading";
import { el, routeLink, topbar } from "../lib";
import { PATHS } from "../../shared/navigation";
import { parseRazManifest, type RazManifest } from "../../shared/raz";

function frame(root: HTMLElement, reader = false): HTMLElement {
  document.title = "RAZ 分级阅读 · 小朋友学习乐园";
  root.appendChild(topbar([{ label: "英语", href: PATHS.english }, { label: "RAZ 分级阅读", href: PATHS.raz }, { label: "A 级", ...(reader ? { href: PATHS.razA } : {}) }]));
  const main = el("main", reader ? "raz-reader" : "wrap main-pad");
  root.appendChild(main);
  return main;
}
async function manifest(): Promise<RazManifest> {
  const res = await fetch("/api/raz/manifest");
  if (!res.ok) throw new Error("暂时无法加载书目，请稍后重试。");
  return parseRazManifest(await res.json());
}
async function signedUrl(key: string): Promise<string> {
  const res = await fetch(`/api/sign?material=raz&k=${encodeURIComponent(key)}`);
  if (!res.ok) throw new Error("资源暂时无法打开，请重试。");
  const data = await res.json() as { url?: string };
  if (!data.url) throw new Error("资源地址不可用。");
  return data.url;
}
function showError(main: HTMLElement, err: unknown): void {
  main.appendChild(el("p", "hint", err instanceof Error ? err.message : "加载失败，请重试。"));
  const retry = el("button", "tab", "重试");
  retry.addEventListener("click", () => window.location.reload());
  main.appendChild(retry);
}
export function razView(root: HTMLElement): void {
  const main = frame(root);
  main.appendChild(el("h2", "section-title", "选择阅读级别"));
  const card = routeLink(PATHS.razA, "material-card mc-english");
  card.appendChild(el("div", "mc-emoji", "📚"));
  const body = el("div", "mc-body");
  body.appendChild(el("div", "mc-name", "A 级"));
  body.appendChild(el("div", "mc-sub", "绘本 · 音频 · 视频"));
  card.appendChild(body);
  main.appendChild(card);
}
export async function razBooksView(root: HTMLElement): Promise<void> {
  const main = frame(root);
  const loading = el("p", "hint", "正在加载书目…");
  main.appendChild(loading);
  try {
    const m = await manifest();
    if (!main.isConnected) return;
    loading.remove();
    main.appendChild(el("h2", "section-title", `A 级 · ${m.books.length} 篇`));
    const search = document.createElement("input");
    search.type = "search";
    search.placeholder = "搜索标题或编号";
    search.setAttribute("aria-label", "搜索绘本");
    search.className = "raz-search";
    main.appendChild(search);
    const count = el("p", "hint");
    const grid = el("div", "grid");
    main.append(count, grid);
    const render = () => {
      grid.replaceChildren();
      const q = search.value.trim().toLowerCase();
      const books = m.books.filter(b => `${b.id} ${b.title}`.toLowerCase().includes(q)).sort((a,b) => Number(a.id)-Number(b.id));
      count.textContent = books.length ? `共 ${books.length} 篇 · 点卡片开始阅读` : "没有找到匹配的绘本";
      for (const b of books) {
        const card = routeLink(`${PATHS.razReader}?id=${encodeURIComponent(b.id)}`, "card");
        card.appendChild(el("span", "badge", b.id));
        card.appendChild(el("span", "t", b.title));
        card.appendChild(el("span", "go", [b.pdf && "📖 绘本", b.audio && "🎧 音频", b.video && "▶ 视频"].filter(Boolean).join(" · ")));
        grid.appendChild(card);
      }
    };
    search.addEventListener("input", render);
    render();
    if (m.collectionAudio) {
      const section = el("section", "raz-collection");
      section.appendChild(el("h2", "section-title", "A 级合集音频"));
      const button = el("button", "tab", "🎧 打开合集音频") as HTMLButtonElement;
      section.appendChild(button);
      main.appendChild(section);
      button.addEventListener("click", async () => {
        button.disabled = true;
        try {
          const url = await signedUrl(m.collectionAudio!);
          if (!section.isConnected) return;
          const audio = document.createElement("audio");
          audio.controls = true; audio.preload = "none"; audio.src = url;
          section.appendChild(audio);
          button.remove();
        } catch (err) { button.disabled = false; showError(section, err); }
      });
    }
  } catch (err) { if (main.isConnected) { loading.remove(); showError(main, err); } }
}
export async function razReaderView(root: HTMLElement): Promise<void> {
  const main = frame(root, true);
  const loading = el("p", "hint", "正在加载绘本…");
  main.appendChild(loading);
  try {
    const m = await manifest();
    if (!main.isConnected) return;
    loading.remove();
    const book = m.books.find(b => b.id === new URLSearchParams(location.search).get("id"));
    if (!book) { main.appendChild(el("p", "hint", "没有找到这篇绘本")); return; }
    document.title = `${book.title} · RAZ A 级`;
    const sidebar = el("aside", "raz-sidebar");
    sidebar.appendChild(el("h2", "raz-reader-title", `${book.id} ${book.title}`));
    const tabs = el("nav", "raz-mode-nav");
    tabs.setAttribute("aria-label", "选择绘本、音频或视频");
    sidebar.appendChild(tabs);
    const content = el("section", "raz-content");
    content.setAttribute("aria-label", book.title);
    main.append(sidebar, content);
    let generation = 0;
    let disposePdf: (() => void) | undefined;
    const choices: HTMLButtonElement[] = [];
    async function open(kind: "pdf" | "audio" | "video", button: HTMLButtonElement) {
      const current = ++generation;
      disposePdf?.();
      disposePdf = undefined;
      content.querySelectorAll("audio,video").forEach(node => (node as HTMLMediaElement).pause());
      content.replaceChildren(el("p", "hint", "正在打开…"));
      choices.forEach(b => { b.classList.toggle("is-on", b === button); b.setAttribute("aria-pressed", String(b === button)); });
      try {
        const url = await signedUrl(book![kind]!);
        if (!content.isConnected || current !== generation) return;
        content.replaceChildren();
        if (kind === "pdf") {
          disposePdf = mountRazReading(content, book!, url, signedUrl);
        } else {
          const media = document.createElement(kind);
          media.controls = true; media.preload = "metadata"; media.src = url;
          if (media instanceof HTMLVideoElement) media.playsInline = true;
          let retried = false;
          media.addEventListener("error", async () => {
            if (current !== generation || !media.isConnected) return;
            if (retried) { showError(content, new Error("播放失败，请重新打开资源。")); return; }
            retried = true;
            try {
              const fresh = await signedUrl(book![kind]!);
              if (current === generation && media.isConnected) { media.src = fresh; media.load(); }
            } catch (err) { if (current === generation && content.isConnected) showError(content, err); }
          });
          content.appendChild(media);
        }
      } catch (err) { if (current === generation && content.isConnected) { content.replaceChildren(); showError(content, err); } }
    }
    for (const [kind, label] of [["pdf", "📖 绘本"], ["audio", "🎧 音频"], ["video", "▶ 视频"]] as const) {
      if (!book[kind]) continue;
      const button = el("button", "raz-mode-button", label) as HTMLButtonElement;
      button.type = "button";
      button.addEventListener("click", () => { void open(kind, button); });
      choices.push(button); tabs.appendChild(button);
    }
    choices[0]?.click();
  } catch (err) { if (main.isConnected) { loading.remove(); showError(main, err); } }
}
